import axios from 'axios';
import config from '@config';
import {
  decodeToken,
  getToken,
  replaceToken,
  isJWTExpired,
  responseFormatter,
  onTokenUpdate,
} from '@utils';
import { validateJwks } from './rc-api';
import { sessionExpireMsg } from '@shared/constants';

const noop = () => {
  return;
};
let isRefreshing = false;
let refreshSubscribers = [];
let isFetchingJWKS = null;
let startTime = Date.now();
let remaining = 100;
let requestWindow = 60;
let limit = 60;

const updateStartTime = async (rateLimitHook = noop) => {
  let delay = 0;
  let now = Date.now();

  let diff = now - startTime;
  if (remaining < 2) {
    delay = requestWindow * 1000 - diff;
    window.isRateLimited = true;
    window.rateLimitDelay = delay;
    rateLimitHook({
      delay,
      remaining,
      requestWindow,
      startTime,
      limit,
    });
    setTimeout(() => {
      window.isRateLimited = false;
      window.rateLimitDelay = 0;
    }, delay);
  } else {
    window.isRateLimited = false;
    window.rateLimitDelay = 0;
  }

  await new Promise(resolve => setTimeout(resolve, delay));
  return now + delay;
};

const apiInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_APP_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

window.isRateLimited = false;
window.rateLimitDelay = 0;

apiInstance.interceptors.request.use(config => {
  if (config.setAuthHeader) {
    const token = getToken();
    config.headers.common['Authorization'] = token;
  }
  if (config.setTimeout) {
    config.timeout = config.setTimeout;
  }

  return config;
});

// Add a request interceptor
apiInstance.interceptors.request.use(
  async config => {
    // Do something before request is sent
    const originalRequest = config;
    let token = getToken();
    if (
      isJWTExpired(token) &&
      !/login/i.test(originalRequest.url) &&
      !/refreshtoken/i.test(originalRequest.url) &&
      !/profile-image/i.test(originalRequest.url)
    ) {
      if (!isRefreshing && process.env?.NODE_ENV !== 'test') {
        isRefreshing = true;
        const { data } = await refreshAccessToken(token);

        if (!data || !data.accessToken)
          return Promise.reject({ message: sessionExpireMsg });

        replaceToken(data.accessToken);
        isRefreshing = false;
        onRrefreshed(data.accessToken);
      }

      subscribeTokenRefresh(token => {
        // replace the expired token and retry
        originalRequest.headers['Authorization'] = token;

        return originalRequest;
      });

      return config;
    } else {
      return config;
    }
  },
  error => {
    // Do something with request error
    return Promise.reject('Session expired. Redirecting to login screen...');
  }
);

const subscribeTokenRefresh = cb => {
  refreshSubscribers.push(cb);
};

const onRrefreshed = token => {
  refreshSubscribers.forEach(cb => {
    cb(token);
  });
  refreshSubscribers = [];
};

const refreshAccessToken = async token => {
  return await responseFormatter(
    apiInstance.get('refreshtoken', { headers: { Authorization: token } })
  );
};

export default apiInstance;

export const rcApi = axios.create({
  baseURL: config.rcApiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

rcApi.interceptors.request.use(async config => {
  startTime = await updateStartTime();
  return config;
});

rcApi.interceptors.response.use(async response => {
  if (response.headers['x-rate-limit-remaining']) {
    remaining = response.headers['x-rate-limit-remaining'];
    console.log('RESPONSE', response.headers['x-rate-limit-remaining']);
  }
  if (response.headers['x-rate-limit-window']) {
    requestWindow = response.headers['x-rate-limit-window'];
  }
  if (response.headers['x-rate-limit-limit']) {
    limit = response.headers['x-rate-limit-limit'];
    console.log('LIMIT', response.headers['x-rate-limit-limit']);
  }
  return response;
});

rcApi.interceptors.request.use(config => {
  if (config.setAuthHeader) {
    const token = getToken();
    config.headers.common['Authorization'] = token;
  }

  return config;
});

rcApi.interceptors.request.use(
  async config => {
    // Do something before request is sent
    const originalRequest = config;
    let token = getToken();
    if (isJWTExpired(token)) {
      subscribeTokenRefresh(token => {
        // replace the expired token and retry
        const decodedToken = decodeToken(token);
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${decodedToken.rc_access_token}`;

        if (originalRequest.headers.access_token) {
          originalRequest.headers.access_token = decodedToken.rc_access_token;
        }

        return originalRequest;
      });

      if (!isRefreshing && process.env?.NODE_ENV !== 'test') {
        isRefreshing = true;

        const { data } = await refreshAccessToken(token);

        if (!data || !data.accessToken)
          return Promise.reject({ message: sessionExpireMsg });

        replaceToken(data.accessToken);
        isRefreshing = false;
        onRrefreshed(data.accessToken);
      }

      return config;
    } else {
      if (!config.hasToken) {
        const decodedToken = decodeToken(getToken());
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${decodedToken.rc_access_token}`;
      }
      return config;
    }
  },
  error => {
    // Redirect user to login screen.
    return Promise.reject('Session expired. Redirecting to login screen...');
  }
);

export const jwksApi = axios.create({
  baseURL: config.rcApiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

jwksApi.interceptors.request.use(async config => {
  startTime = await updateStartTime(config.hooks?.rateLimitReached || noop);
  return config;
});

jwksApi.interceptors.response.use(async response => {
  if (response.headers['x-rate-limit-remaining']) {
    remaining = response.headers['x-rate-limit-remaining'];
    console.log('RESPONSE', response.headers['x-rate-limit-remaining']);
  }
  if (response.headers['x-rate-limit-window']) {
    requestWindow = response.headers['x-rate-limit-window'];
  }
  if (response.headers['x-rate-limit-limit']) {
    limit = response.headers['x-rate-limit-limit'];
    console.log('LIMIT', response.headers['x-rate-limit-limit']);
  }
  return response;
});

jwksApi.interceptors.request.use(async config => {
  const { hasToken, accountUid } = config;
  if (hasToken) {
    const isValidToken = await validateJwks(hasToken, accountUid);
    if (isValidToken.error) {
      onTokenUpdate();
      return Promise.reject(isValidToken.error);
    }

    config.headers['Authorization'] = `Bearer ${
      isValidToken.token && isValidToken.token.accessToken
    }`;

    if (isValidToken.hasUpdatedToken) {
      config.hasUpdatedToken = true;
      config.updatedToken = isValidToken.token;
    }

    return config;
  } else {
    const decodedToken = decodeToken(getToken());
    config.headers['Authorization'] = `Bearer ${decodedToken.rc_access_token}`;
  }
  return config;
});

jwksApi.interceptors.response.use(
  async response => {
    if (response.config.hasUpdatedToken) {
      response.data = {
        ...response.data,
        token: response.config.updatedToken,
      };
    }
    return response;
  },
  error => {
    if (error.config && error.config.hasToken) {
      error.origin = 'JWKS';
      if (error.response && error.response.status === 401) {
        const getNewToken =
          isFetchingJWKS || onTokenUpdate(null, error.config.accountUid);
        isFetchingJWKS = getNewToken;
        return (
          getNewToken &&
          getNewToken
            .then(token => {
              if (token) {
                error.config.headers.Authorization = `Bearer ${token.accessToken}`;
                error.config.hasToken = token;
                setTimeout(() => (isFetchingJWKS = null), 700);
                return jwksApi.request(error.config);
              }
            })
            .catch(tokenError => {
              Promise.reject(tokenError);
            })
        );
      }
    }
    return Promise.reject(error);
  }
);
